/**
 * Hook for managing catalog filters state with localStorage persistence
 */

import { useState, useCallback, useMemo, useEffect } from 'react';

export type SortOption = 'created_at:desc' | 'created_at:asc' | 'age:young' | 'age:old' | 'popularity:desc';

export interface CatalogFiltersState {
  speciesKey: string | null;
  breedIds: string[];
  breedNames: Record<string, string>; // { breedId: breedName } for UI display
  sex: string[];
  ageBuckets: string[];
  geoPlaceId: string | null;
  geoPlaceName: string | null;
  textSearch: string | null;
  onlyWithPhotos: boolean;
  sort: SortOption;
  // Extended filters (v2)
  sizes: string[];
  colors: string[];
  healthSpecialNeeds: boolean | null;
  characterTypes: string[]; // 'introvert' | 'extrovert'
}

const STORAGE_KEY = 'catalog_filters';

const DEFAULT_FILTERS: CatalogFiltersState = {
  speciesKey: null,
  breedIds: [],
  breedNames: {},
  sex: [],
  ageBuckets: [],
  geoPlaceId: null,
  geoPlaceName: null,
  textSearch: null,
  onlyWithPhotos: false,
  sort: 'created_at:desc',
  // Extended filters (v2)
  sizes: [],
  colors: [],
  healthSpecialNeeds: null,
  characterTypes: [],
};

/**
 * Load saved filters from localStorage
 */
function loadSavedFilters(): CatalogFiltersState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_FILTERS, ...parsed };
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_FILTERS;
}

/**
 * Save filters to localStorage
 */
function saveFilters(filters: CatalogFiltersState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

export interface UseCatalogFiltersReturn {
  filters: CatalogFiltersState;
  activeFiltersCount: number;
  setSpeciesKey: (key: string | null) => void;
  toggleBreedId: (id: string) => void;
  setBreedIds: (ids: string[]) => void;
  toggleBreedWithName: (id: string, name: string) => void;
  removeBreed: (id: string) => void;
  setBreedNames: (names: Record<string, string>) => void;
  toggleSex: (sex: string) => void;
  setSex: (sex: string[]) => void;
  toggleAgeBucket: (bucket: string) => void;
  setAgeBuckets: (buckets: string[]) => void;
  setGeoPlaceId: (id: string | null, name?: string | null) => void;
  setTextSearch: (query: string | null) => void;
  setOnlyWithPhotos: (value: boolean) => void;
  setSort: (sort: SortOption) => void;
  resetFilters: () => void;
  restoreFromStorage: () => void;
  hasActiveFilters: boolean;
  // Extended filters (v2)
  setSizes: (sizes: string[]) => void;
  setColors: (colors: string[]) => void;
  setHealthSpecialNeeds: (value: boolean | null) => void;
  setCharacterTypes: (types: string[]) => void;
}

interface UseCatalogFiltersOptions {
  persist?: boolean;
  initialFilters?: Partial<CatalogFiltersState>;
}

export function useCatalogFilters(options: UseCatalogFiltersOptions = {}): UseCatalogFiltersReturn {
  const { persist = true, initialFilters } = options;
  
  // Initialize from localStorage if persist is true, otherwise use defaults/initial
  const [filters, setFilters] = useState<CatalogFiltersState>(() => {
    if (initialFilters) {
      return { ...DEFAULT_FILTERS, ...initialFilters };
    }
    if (persist) {
      return loadSavedFilters();
    }
    return DEFAULT_FILTERS;
  });

  // Save to localStorage when filters change (if persist is enabled)
  useEffect(() => {
    if (persist) {
      saveFilters(filters);
    }
  }, [filters, persist]);

  const setSpeciesKey = useCallback((key: string | null) => {
    setFilters(prev => {
      let newAgeBuckets = prev.ageBuckets;
      
      // Reset conflicting age filters when species changes
      if (key === 'dog' && prev.ageBuckets.includes('kitten')) {
        newAgeBuckets = prev.ageBuckets.filter(b => b !== 'kitten');
      }
      if (key === 'cat' && prev.ageBuckets.includes('puppy')) {
        newAgeBuckets = prev.ageBuckets.filter(b => b !== 'puppy');
      }
      
      // Clear breeds when species changes
      return { ...prev, speciesKey: key, breedIds: [], breedNames: {}, ageBuckets: newAgeBuckets };
    });
  }, []);

  const toggleBreedId = useCallback((id: string) => {
    setFilters(prev => ({
      ...prev,
      breedIds: prev.breedIds.includes(id)
        ? prev.breedIds.filter(b => b !== id)
        : [...prev.breedIds, id],
    }));
  }, []);

  const setBreedIds = useCallback((ids: string[]) => {
    setFilters(prev => ({ ...prev, breedIds: ids }));
  }, []);

  const toggleBreedWithName = useCallback((id: string, name: string) => {
    setFilters(prev => {
      const isSelected = prev.breedIds.includes(id);
      const newBreedIds = isSelected
        ? prev.breedIds.filter(b => b !== id)
        : [...prev.breedIds, id];
      
      const newBreedNames = { ...prev.breedNames };
      if (!isSelected) {
        newBreedNames[id] = name;
      } else {
        delete newBreedNames[id];
      }
      
      return { ...prev, breedIds: newBreedIds, breedNames: newBreedNames };
    });
  }, []);

  const removeBreed = useCallback((id: string) => {
    setFilters(prev => {
      const newBreedNames = { ...prev.breedNames };
      delete newBreedNames[id];
      return {
        ...prev,
        breedIds: prev.breedIds.filter(b => b !== id),
        breedNames: newBreedNames,
      };
    });
  }, []);

  const setBreedNames = useCallback((names: Record<string, string>) => {
    setFilters(prev => ({ ...prev, breedNames: names }));
  }, []);

  const toggleSex = useCallback((sex: string) => {
    setFilters(prev => ({
      ...prev,
      sex: prev.sex.includes(sex)
        ? prev.sex.filter(s => s !== sex)
        : [...prev.sex, sex],
    }));
  }, []);

  const setSex = useCallback((sex: string[]) => {
    setFilters(prev => ({ ...prev, sex }));
  }, []);

  const toggleAgeBucket = useCallback((bucket: string) => {
    setFilters(prev => ({
      ...prev,
      ageBuckets: prev.ageBuckets.includes(bucket)
        ? prev.ageBuckets.filter(b => b !== bucket)
        : [...prev.ageBuckets, bucket],
    }));
  }, []);

  const setAgeBuckets = useCallback((buckets: string[]) => {
    setFilters(prev => ({ ...prev, ageBuckets: buckets }));
  }, []);

  const setGeoPlaceId = useCallback((id: string | null, name?: string | null) => {
    setFilters(prev => ({ ...prev, geoPlaceId: id, geoPlaceName: name ?? null }));
  }, []);

  const setTextSearch = useCallback((query: string | null) => {
    setFilters(prev => ({ ...prev, textSearch: query }));
  }, []);

  const setOnlyWithPhotos = useCallback((value: boolean) => {
    setFilters(prev => ({ ...prev, onlyWithPhotos: value }));
  }, []);

  const setSort = useCallback((sort: SortOption) => {
    setFilters(prev => ({ ...prev, sort }));
  }, []);

  // Extended filters (v2)
  const setSizes = useCallback((sizes: string[]) => {
    setFilters(prev => ({ ...prev, sizes }));
  }, []);

  const setColors = useCallback((colors: string[]) => {
    setFilters(prev => ({ ...prev, colors }));
  }, []);

  const setHealthSpecialNeeds = useCallback((value: boolean | null) => {
    setFilters(prev => ({ ...prev, healthSpecialNeeds: value }));
  }, []);

  const setCharacterTypes = useCallback((types: string[]) => {
    setFilters(prev => ({ ...prev, characterTypes: types }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const restoreFromStorage = useCallback(() => {
    setFilters(loadSavedFilters());
  }, []);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.speciesKey) count++;
    if (filters.breedIds.length > 0) count++;
    if (filters.sex.length > 0) count++;
    if (filters.ageBuckets.length > 0) count++;
    if (filters.geoPlaceId) count++;
    if (filters.onlyWithPhotos) count++;
    // Extended filters (v2)
    if (filters.sizes.length > 0) count++;
    if (filters.colors.length > 0) count++;
    if (filters.healthSpecialNeeds !== null) count++;
    if (filters.characterTypes.length > 0) count++;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFiltersCount > 0;

  return {
    filters,
    activeFiltersCount,
    setSpeciesKey,
    toggleBreedId,
    setBreedIds,
    toggleBreedWithName,
    removeBreed,
    setBreedNames,
    toggleSex,
    setSex,
    toggleAgeBucket,
    setAgeBuckets,
    setGeoPlaceId,
    setTextSearch,
    setOnlyWithPhotos,
    setSort,
    resetFilters,
    restoreFromStorage,
    hasActiveFilters,
    // Extended filters (v2)
    setSizes,
    setColors,
    setHealthSpecialNeeds,
    setCharacterTypes,
  };
}